import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../AuthContext';
import { 
  LayoutDashboard, 
  Film, 
  MapPin, 
  Calendar, 
  Tag, 
  CalendarDays,
  Image,
  FileText,
  Settings,
  LogOut,
  Award,
  Mail,
  MessageSquare,
  Menu,
  X,
  Database,
  BarChart3,
  RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', title: 'Dashboard' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analitik', title: 'Gelişmiş Analitik' },
    { path: '/admin/movies', icon: Film, label: 'Filmler', title: 'Film Yönetimi' },
    { path: '/admin/showtimes', icon: Calendar, label: 'Seanslar', title: 'Seans Yönetimi' },
    { path: '/admin/branches', icon: MapPin, label: 'Şubeler', title: 'Şube Yönetimi' },
    { path: '/admin/campaigns', icon: Tag, label: 'Kampanyalar', title: 'Kampanya Yönetimi' },
    { path: '/admin/events', icon: CalendarDays, label: 'Haberler', title: 'Haber Yönetimi' },
    { path: '/admin/gallery', icon: Image, label: 'Galeri', title: 'Galeri Yönetimi' },
    { path: '/admin/smart-ratings', icon: Award, label: 'Akıllı İşaretler', title: 'Akıllı İşaret Yönetimi' },
    { path: '/admin/contact-messages', icon: Mail, label: 'İletişim Mesajları', title: 'İletişim Mesajları' },
    { path: '/admin/contact-subjects', icon: MessageSquare, label: 'İletişim Konuları', title: 'İletişim Konuları' },
    { path: '/admin/pages', icon: FileText, label: 'Sayfalar', title: 'Sayfa Yönetimi' },
    { path: '/admin/settings', icon: Settings, label: 'Ayarlar', title: 'Site Ayarları' },
    { path: '/admin/biletinial-sync', icon: RefreshCw, label: 'Biletinial Senkron', title: 'Biletinial Senkronizasyonu' },
    { path: '/admin/database-export', icon: Database, label: 'Veritabanı Yedeği', title: 'Veritabanı Yedekleme' },
  ];

  const currentPageTitle = useMemo(() => {
    const currentItem = menuItems.find(item => item.path === location.pathname);
    return currentItem ? currentItem.title : 'Yönetim Paneli';
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      <Helmet>
        <title>{currentPageTitle} - Cinefora Admin</title>
      </Helmet>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-gray-900 p-3 rounded-lg border border-gray-800 text-white hover:bg-gray-800 transition-colors"
      >
        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeMobileMenu}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        w-64 bg-gray-900 border-r border-gray-800 flex flex-col
        fixed lg:static inset-y-0 left-0 z-40 transform transition-transform duration-300
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-gray-800">
          <h1 className="text-2xl font-bold text-white">Cinefora</h1>
          <p className="text-sm text-gray-400 mt-1">Yönetim Paneli</p>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMobileMenu}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                  isActive
                    ? 'bg-[#FDB714] text-black font-semibold'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-800">
          <div className="mb-3 px-4">
            <p className="text-sm text-gray-400">Giriş yapan:</p>
            <p className="text-white font-semibold">{user?.username}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            <LogOut size={16} className="mr-2" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-4 lg:p-8 pt-20 lg:pt-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
